import React from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Check,
  CalendarCheck,
  Info,
  MapPinLine,
  Plus,
  Star,
  Crown,
} from "@phosphor-icons/react";
import {
  SERVICE_CATEGORIES,
  BUNDLE_PACKAGES,
  ADD_ONS,
  PRICING_NOTE,
  TRAVEL_FEES,
} from "../data";

export default function Pricing() {
  return (
    <section id="services" data-testid="pricing-section" className="bg-[#050505]">
      {/* ============== INTRO ============== */}
      <div className="container-pd pt-24 md:pt-32 pb-12">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 mb-5">
            <div className="divider-gold" />
            <span className="eyebrow">Our Services</span>
          </div>
          <h2 className="font-display font-black text-white text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1.02]">
            Two disciplines. <span className="text-[#D4AF37]">One uncompromising standard.</span>
          </h2>
          <p className="mt-5 text-zinc-400 text-base sm:text-lg leading-relaxed">
            Select by need — refresh the everyday or restore the neglected.
            Every detail is hand-finished by a senior technician and inspected before we leave.
          </p>
        </div>
      </div>

      {/* ============== SERVICE CATEGORIES (stacked sections) ============== */}
      {SERVICE_CATEGORIES.map((cat, ci) => (
        <CategoryBlock key={cat.key} category={cat} index={ci} />
      ))}

      {/* ============== BUNDLE & SAVE ============== */}
      <BundleSection />

      {/* ============== ADD-ONS · TRAVEL · NOTE · CTA ============== */}
      <div className="container-pd pt-24 pb-28">
        {/* Add-Ons */}
        <div data-testid="addons-block" className="mb-20">
          <div className="flex items-center gap-3 mb-5">
            <div className="divider-gold" />
            <span className="eyebrow">Add-Ons</span>
          </div>
          <h3 className="font-display font-bold text-2xl md:text-3xl text-white">Pair any service with extras.</h3>

          <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/5 border border-white/5">
            {ADD_ONS.map((a, i) => (
              <motion.div
                key={a.name}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                data-testid={`addon-${i}`}
                className="bg-[#0a0a0a] p-6 flex items-center justify-between gap-4 hover:bg-[#0d0d0d] transition-colors"
              >
                <div className="flex items-start gap-3">
                  <Plus size={18} weight="bold" className="text-[#D4AF37] mt-0.5" />
                  <span className="text-white text-sm md:text-base">{a.name}</span>
                </div>
                <div className="font-display font-bold text-lg text-white whitespace-nowrap">From ${a.price}</div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Travel Fees */}
        <div id="travel" data-testid="travel-block" className="scroll-mt-24 mb-12">
          <div className="flex items-center gap-3 mb-5">
            <div className="divider-gold" />
            <span className="eyebrow">Travel Fees</span>
          </div>
          <h3 className="font-display font-bold text-2xl md:text-3xl text-white">We come to your driveway.</h3>
          <p className="text-zinc-400 text-sm mt-2 max-w-2xl">Flat-rate, transparent pricing — measured from our base location.</p>

          <div className="mt-7 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
            {TRAVEL_FEES.map((row, i) => (
              <motion.div
                key={row.range}
                data-testid={`travel-row-${i}`}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`bg-[#0a0a0a] p-6 ${row.highlight ? "ring-1 ring-inset ring-[#D4AF37]/30" : ""}`}
              >
                <div className="flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-semibold">
                  <MapPinLine size={14} weight="bold" className="text-[#D4AF37]" /> Distance
                </div>
                <div className="text-white text-sm md:text-base mt-2">{row.range}</div>
                <div className={`mt-3 font-display font-bold text-2xl ${row.highlight ? "text-[#D4AF37]" : "text-white"}`}>
                  {row.fee}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Pricing Note */}
        <motion.div
          data-testid="pricing-note"
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-10 flex items-start gap-3 p-5 border border-white/5 bg-[#0c0c0c]"
        >
          <Info size={18} weight="bold" className="text-[#D4AF37] shrink-0 mt-0.5" />
          <p className="text-sm text-zinc-400 leading-relaxed">{PRICING_NOTE}</p>
        </motion.div>

        {/* Bottom CTAs */}
        <div className="mt-10 flex flex-col sm:flex-row gap-4">
          <a href="#quote" data-testid="pricing-quote-btn" className="btn-primary">
            Request a Quote <ArrowRight size={16} weight="bold" />
          </a>
          <a href="#quote" data-testid="pricing-book-btn" className="btn-secondary">
            <CalendarCheck size={16} weight="bold" /> Book Now
          </a>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// CATEGORY BLOCK — Exterior Care / Interior Care
// ============================================================
function CategoryBlock({ category, index }) {
  return (
    <div
      data-testid={`category-${category.key}`}
      className={`relative ${index % 2 === 0 ? "bg-[#050505]" : "bg-[#0a0a0a]"} ${index !== 0 ? "border-t border-white/5" : ""}`}
    >
      <div className="container-pd py-20 md:py-24">
        <div className="flex items-end justify-between gap-4 mb-10 md:mb-14">
          <div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#D4AF37] font-semibold">{`0${index + 1}`} · {category.label}</div>
            <h3 className="mt-2 font-display font-black text-white text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1.05]">
              {category.headline}
            </h3>
            <p className="mt-3 text-zinc-400 text-base sm:text-lg max-w-xl">{category.description}</p>
          </div>
          <div className="hidden md:block text-xs text-zinc-500 whitespace-nowrap">{category.packages.length} packages</div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {category.packages.map((pkg, i) => (
            <PackageCard key={pkg.slug} pkg={pkg} index={i} />
          ))}
        </div>
      </div>
    </div>
  );
}

function PackageCard({ pkg, index }) {
  const isFeatured = pkg.featured;
  return (
    <motion.article
      data-testid={`package-card-${pkg.slug}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.55, delay: index * 0.08 }}
      className={`card-luxe group flex flex-col ${isFeatured ? "ring-1 ring-[#D4AF37]/40" : ""}`}
    >
      <div className="relative h-52 md:h-60 overflow-hidden">
        <img
          src={pkg.image}
          alt={pkg.name}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0c0c0c] via-[#0c0c0c]/30 to-transparent" />
        {isFeatured && (
          <div className="absolute top-4 left-4 bg-[#D4AF37] text-[#050505] text-[10px] font-bold tracking-[0.2em] uppercase px-3 py-1.5 inline-flex items-center gap-1.5">
            <Star size={11} weight="fill" /> Recommended
          </div>
        )}
        <div className="absolute bottom-4 right-4 text-right">
          <div className="text-[10px] text-zinc-300 uppercase tracking-[0.2em]">From</div>
          <div className="font-display text-3xl font-black text-white">${pkg.price}</div>
        </div>
      </div>

      <div className="p-7 md:p-8 flex-1 flex flex-col">
        <h4 className="font-display font-bold text-2xl text-white tracking-tight">{pkg.name}</h4>
        <p className="text-[#D4AF37] mt-2 text-sm font-medium italic">{pkg.tagline}</p>

        {pkg.description && (
          <p className="text-zinc-400 mt-4 text-sm leading-relaxed">{pkg.description}</p>
        )}

        <ul className="mt-6 space-y-2.5 flex-1">
          {pkg.features.map((f) => (
            <li key={f} className="flex items-start gap-3 text-sm text-zinc-300">
              <Check size={15} weight="bold" className="text-[#D4AF37] mt-0.5 shrink-0" />
              <span>{f}</span>
            </li>
          ))}
        </ul>

        <a
          href="#quote"
          data-testid={`package-quote-${pkg.slug}`}
          className="mt-7 btn-primary !py-3.5 !px-5 text-xs justify-center"
        >
          Request a Quote <ArrowRight size={14} weight="bold" />
        </a>
      </div>
    </motion.article>
  );
}

// ============================================================
// BUNDLE & SAVE — visually distinct premium section
// ============================================================
function BundleSection() {
  return (
    <div
      data-testid="bundle-section"
      className="relative border-t border-[#D4AF37]/20 bg-gradient-to-b from-[#0a0a0a] via-[#080808] to-[#050505] overflow-hidden"
    >
      {/* subtle gold glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          background:
            "radial-gradient(800px circle at 80% 0%, rgba(212,175,55,1) 0%, transparent 60%)",
        }}
      />
      <div className="grain" />

      <div className="container-pd py-28 md:py-36 relative">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="divider-gold" />
            <span className="eyebrow">Bundle &amp; Save</span>
          </div>
          <h3 className="font-display font-black text-white text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02]">
            Complete <span className="text-[#D4AF37]">Vehicle Care.</span>
          </h3>
          <p className="mt-6 text-zinc-400 text-base sm:text-lg leading-relaxed max-w-2xl">
            Whether your vehicle needs regular maintenance or a complete transformation,
            our bundled detailing packages deliver premium care inside and out with one
            convenient booking.
          </p>
        </div>

        <div className="mt-20 md:mt-24 grid grid-cols-1 lg:grid-cols-2 gap-8">
          {BUNDLE_PACKAGES.map((pkg, i) => (
            <BundleCard key={pkg.slug} pkg={pkg} index={i} />
          ))}
        </div>
      </div>
    </div>
  );
}

function BundleCard({ pkg, index }) {
  const isFeatured = pkg.featured;
  const BadgeIcon = pkg.badgeIcon === "crown" ? Crown : Star;
  return (
    <motion.article
      data-testid={`bundle-card-${pkg.slug}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className={`relative group overflow-hidden border ${
        isFeatured ? "border-[#D4AF37]/50" : "border-white/10"
      } bg-[#0a0a0a]/60 backdrop-blur-sm transition-all duration-500 hover:border-[#D4AF37]/70`}
    >
      <div className="grid grid-cols-1 lg:grid-cols-5 h-full">
        {/* Image side */}
        <div className="relative lg:col-span-2 h-60 lg:h-auto overflow-hidden">
          <img
            src={pkg.image}
            alt={pkg.name}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a]/40 via-transparent to-[#0a0a0a]/60 lg:bg-gradient-to-l lg:from-[#0a0a0a] lg:to-transparent" />
          {pkg.badge && (
            <div
              className={`absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold tracking-[0.22em] uppercase ${
                isFeatured
                  ? "bg-[#D4AF37] text-[#050505]"
                  : "bg-[#050505]/80 backdrop-blur-sm text-[#D4AF37] border border-[#D4AF37]/40"
              }`}
            >
              <BadgeIcon size={11} weight="fill" />
              {pkg.badge}
            </div>
          )}
        </div>

        {/* Content side */}
        <div className="lg:col-span-3 p-8 md:p-10 flex flex-col">
          <div className="flex items-start justify-between gap-6">
            <div className="flex-1 min-w-0">
              <h4 className="font-display font-bold text-white tracking-tight text-3xl md:text-4xl leading-[1.05]">
                {pkg.name}
              </h4>
              <p className="text-zinc-500 mt-3 text-xs md:text-sm font-light italic tracking-wide">
                {pkg.tagline}
              </p>
            </div>
            <div className="text-right shrink-0">
              <div className="text-[10px] text-zinc-400 uppercase tracking-[0.25em]">From</div>
              <div className="font-display text-3xl md:text-4xl font-black text-white mt-1">${pkg.price}</div>
            </div>
          </div>

          <p className="text-zinc-400 mt-7 text-sm leading-relaxed">{pkg.description}</p>

          <div className="mt-8 pt-6 border-t border-white/5">
            <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold mb-4">Includes</div>
            <ul className="space-y-2.5">
              {pkg.includes.map((item) => (
                <li key={item} className="flex items-center gap-3 text-sm text-white">
                  <Check size={16} weight="bold" className="text-[#D4AF37]" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <a
            href="#quote"
            data-testid={`bundle-quote-${pkg.slug}`}
            className="mt-8 btn-primary !py-3.5 !px-5 text-xs justify-center"
          >
            Request a Quote <ArrowRight size={14} weight="bold" />
          </a>
        </div>
      </div>
    </motion.article>
  );
}
